import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { z } from "zod";

import { logger } from "@/lib/logger";
import { createKeyedStore } from "@/lib/storage";
import { createCloudflareAdapter } from "@/modules/analytics/adapters/cloudflare";
import { createPlausibleAdapter } from "@/modules/analytics/adapters/plausible";
import type { AnalyticsAdapter, AnalyticsEvent, ConsentStatus } from "@/modules/analytics/types";

const CONSENT_STORAGE_KEY = "apolo-seed:analytics-consent";
const consentSchema = z.enum(["granted", "denied"]);

interface AnalyticsContextValue {
  adapters: AnalyticsAdapter[];
  consent: ConsentStatus;
  page: (path?: string) => void;
  setConsent: (next: "granted" | "denied") => void;
  track: (event: AnalyticsEvent) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export interface AnalyticsConfig {
  cloudflareToken?: string;
  enabled: boolean;
  plausibleDomain?: string;
}

interface AnalyticsProviderProps {
  children: ReactNode;
  config: AnalyticsConfig;
}

function resolveAdapters(config: AnalyticsConfig): AnalyticsAdapter[] {
  if (!config.enabled) return [];
  const adapters: AnalyticsAdapter[] = [];
  if (config.cloudflareToken) {
    adapters.push(createCloudflareAdapter({ token: config.cloudflareToken }));
  }
  if (config.plausibleDomain) {
    adapters.push(createPlausibleAdapter({ domain: config.plausibleDomain }));
  }
  return adapters;
}

export function AnalyticsProvider({
  children,
  config,
}: AnalyticsProviderProps): JSX.Element {
  const store = useMemo(
    () => createKeyedStore<"granted" | "denied">(CONSENT_STORAGE_KEY, consentSchema),
    [],
  );
  const adapters = useMemo(() => resolveAdapters(config), [config]);
  const location = useLocation();

  const [consent, setConsentState] = useState<ConsentStatus>(() => {
    const stored = store.read();
    if (stored === "granted" || stored === "denied") return stored;
    return adapters.some((adapter) => adapter.requiresConsent) ? "pending" : "granted";
  });

  const initialized = useRef<Set<string>>(new Set());

  const shouldRunAdapter = useCallback(
    (adapter: AnalyticsAdapter): boolean => {
      if (!config.enabled) return false;
      return adapter.requiresConsent ? consent === "granted" : true;
    },
    [config.enabled, consent],
  );

  useEffect(() => {
    for (const adapter of adapters) {
      if (!shouldRunAdapter(adapter)) continue;
      if (initialized.current.has(adapter.id)) continue;
      adapter.init();
      initialized.current.add(adapter.id);
    }
  }, [adapters, shouldRunAdapter]);

  useEffect(() => {
    if (!config.enabled) return;
    const path = location.pathname + location.search;
    for (const adapter of adapters) {
      if (!shouldRunAdapter(adapter)) continue;
      if (!initialized.current.has(adapter.id)) continue;
      adapter.page(path);
    }
    logger.debug("analytics.pageview", { path });
  }, [adapters, config.enabled, location.pathname, location.search, shouldRunAdapter]);

  const track = useCallback(
    (event: AnalyticsEvent) => {
      if (!config.enabled) return;
      for (const adapter of adapters) {
        if (!shouldRunAdapter(adapter)) continue;
        if (!initialized.current.has(adapter.id)) continue;
        adapter.track(event);
      }
    },
    [adapters, config.enabled, shouldRunAdapter],
  );

  const page = useCallback(
    (path?: string) => {
      if (!config.enabled) return;
      const resolvedPath = path ?? location.pathname;
      for (const adapter of adapters) {
        if (!shouldRunAdapter(adapter)) continue;
        if (!initialized.current.has(adapter.id)) continue;
        adapter.page(resolvedPath);
      }
    },
    [adapters, config.enabled, location.pathname, shouldRunAdapter],
  );

  const setConsent = useCallback(
    (next: "granted" | "denied") => {
      store.write(next);
      setConsentState(next);
      if (next === "denied") {
        for (const adapter of adapters) {
          if (adapter.requiresConsent && initialized.current.has(adapter.id)) {
            adapter.shutdown?.();
            initialized.current.delete(adapter.id);
          }
        }
      }
    },
    [adapters, store],
  );

  const value = useMemo<AnalyticsContextValue>(
    () => ({ adapters, consent, page, setConsent, track }),
    [adapters, consent, page, setConsent, track],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used inside an AnalyticsProvider.");
  }
  return context;
}
